import React, {useState, useCallback, useEffect, useRef} from 'react';
import AppBar from '@material-ui/core/AppBar';
import styled from 'styled-components';
import root from 'window-or-global';
import {GridContainer} from '../grid';
import Grid from '@material-ui/core/Grid';
import {SubnavItem, SubnavItemProps} from './SubnavItem';
import {SubnavBackToTopBtn} from './SubnavBackToTopBtn';
import {SubnavDownloadBtn} from './SubnavDownloadBtn';
import throttle from 'lodash.throttle';
import debounce from 'lodash.debounce';
import {subNavHeight} from './Subnav.config';
import {AccentColorOptions, useBreakpointDown, useBreakpointUp} from 'epic-ue-theme';

const Styled = styled.div`
    background-color: ${(p) => p.theme.palette.background.default};
    color: ${(p) => p.theme.palette.text.primary};
`;

const SubNavWrap = styled.div`
    height: ${subNavHeight}px;
    background-color: ${(p) =>
        p.theme.ifLight(p, p.theme.palette.neutral.chalk, p.theme.palette.neutral.concrete)};
    header {
        height: ${subNavHeight}px;
    }
    .ue-grid-container {
        height: 100%;
    }
`;

const SubNavInner = styled.div`
    height: 100%;
    text-align: center;
    position: relative;

    &.overflow {
        overflow-y: hidden;
        overflow-x: auto;
        scrollbar-width: thin;
        &::-webkit-scrollbar {
            height: 5px;
        }
        &::-webkit-scrollbar-track {
            background-color: ${(p) =>
                p.theme.ifLight(p, p.theme.palette.neutral.pearl, p.theme.palette.neutral.steel)};
        }
        &::-webkit-scrollbar-thumb {
            background-color: ${(p) => p.theme.palette.neutral.silver};
            border-radius: 10px;
        }
    }
`;

const OverflowWrap = styled.div<{width: number}>`
    text-align: left;
    width: ${(p) => (p.width ? `${p.width + p.theme.spacing(1)}px` : '100%')};
    display: inline-block;
    &.sticky {
        margin-left: 52px;
    }
`;

const shouldBeSticky = (el) => {
    const rect = el.getBoundingClientRect();
    return rect.bottom < rect.height;
};

const offset = 100;

export interface SubnavProps {
    items: SubnavItemProps[];
    downloadBtnLabel: string;
    backToTopBtnLabel: string;
    accentColor?: AccentColorOptions;
    downloadHref?: string;
}

export const Subnav = (props: SubnavProps): JSX.Element | null => {
    const {downloadBtnLabel, backToTopBtnLabel, accentColor, downloadHref} = props;
    const full = useBreakpointUp('lg');
    const items = props.items || [];
    const [isSticky, setIsSticky] = useState(false);
    const [anchorIdActive, setAnchorIdActive] = useState('');
    const [overflowWidth, setOverflowWidth] = useState(0);
    const [anchorIdHover, setAnchorIdHover] = useState('');
    const subNavEl = useRef(null);
    const isSm = useBreakpointDown('sm');

    const setInnerScrollWidth = () => {
        let totalWidth = 0;
        items.forEach((item, i) => {
            const el = root.document.getElementById(`nav-item-${i}`);
            if (el) {
                totalWidth += el.offsetWidth;
            }
        });
        setOverflowWidth(totalWidth);
    };

    useEffect(() => {
        setInnerScrollWidth();
    }, [setInnerScrollWidth, items]);

    //scroll horizontally to the active nav item
    const smallHorizontalScroll = useCallback(
        (activeIndex) => {
            if (!isSm || activeIndex === undefined) return;

            const navItemEl = root.document.getElementById(`nav-item-${activeIndex}`);
            if (navItemEl) {
                const overflowScrollEl = root.document.getElementById('navScroll');
                if (overflowScrollEl) {
                    const {left} = navItemEl.getBoundingClientRect();
                    overflowScrollEl.scrollTo({
                        left: left + overflowScrollEl.scrollLeft - 52,
                        behavior: 'smooth'
                    });
                }
            }
        },
        [isSm]
    );

    const trackScroll = useCallback(() => {
        /**
         * Determine if the subnav should be in the sticky or static state
         */
        if (subNavEl && subNavEl.current) {
            setIsSticky(shouldBeSticky(subNavEl.current));
        }
    }, [isSticky, setIsSticky]);

    const trackActive = useCallback(() => {
        //if scrolled at the very top or very bottom of the page we can assume which item should be active
        let staticScrollPosition = '';
        const scrollY = root.pageYOffset || root.scrollY;
        if (root.innerHeight + scrollY >= root.document.body.offsetHeight - offset) {
            staticScrollPosition = 'bottom';
        } else if (scrollY + offset <= 0) {
            staticScrollPosition = 'top';
        }

        /**
         * Determine which of the anchorId elements is active
         */
        let newActive = '';
        let activeIndex = 0;
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            const {anchorId} = item;
            if (anchorId) {
                const sectionEl = root.document.getElementById(anchorId);
                if (sectionEl) {
                    const {top, bottom} = sectionEl.getBoundingClientRect();

                    //if scrolled at top set the first item active
                    if (i === 0 && staticScrollPosition === 'top') {
                        newActive = anchorId;
                        activeIndex = i;
                        break;
                    } else if (i === items.length - 1 && staticScrollPosition === 'bottom') {
                        //if scrolled to bottom of page set the last item active
                        newActive = anchorId;
                        activeIndex = i;
                        break;
                    } else if (
                        top <= subNavHeight + offset &&
                        bottom - subNavHeight - offset >= 0
                    ) {
                        //try to determine which section is active
                        newActive = anchorId;
                        activeIndex = i;
                    }
                } else {
                    break;
                }
            }
        }
        if (anchorIdActive !== newActive) {
            setAnchorIdActive(newActive);
            smallHorizontalScroll(newActive ? activeIndex : undefined);
        }
    }, [anchorIdActive, setAnchorIdActive, items]);

    const throttleScroll = throttle(trackScroll, 50);
    const debounceTrackActive = debounce(trackActive, 100, {leading: false, maxWait: 750});
    const throttleResize = debounce(trackScroll, 1000, {leading: false});

    /**
     * After first render add a throttled scroll listener
     */
    useEffect(() => {
        trackScroll();

        root.document.addEventListener('scroll', throttleScroll);
        root.document.addEventListener('scroll', debounceTrackActive);
        root.document.addEventListener('resize', throttleResize);
        return () => {
            root.document.removeEventListener('scroll', throttleScroll);
            root.document.removeEventListener('scroll', debounceTrackActive);
            root.document.removeEventListener('resize', throttleResize);
        };
    }, [trackScroll, trackActive, throttleScroll, throttleResize, subNavEl]);

    const hoverItem = useCallback(
        (item) => () => {
            const {anchorId} = item;
            setAnchorIdHover(anchorId);
        },
        [setAnchorIdHover]
    );

    const leaveItem = useCallback(
        (e) => {
            setAnchorIdHover('');
            e.preventDefault();
        },
        [setAnchorIdHover]
    );

    const subNavItems = items.map((navItem, i) => (
        <SubnavItem
            key={navItem.title}
            {...navItem}
            index={i}
            active={navItem.anchorId === anchorIdActive}
            accentColor={accentColor}
            onMouseEnter={hoverItem(navItem)}
            onMouseLeave={leaveItem}
            isHoverOther={Boolean(anchorIdHover && navItem.anchorId !== anchorIdHover)}
        />
    ));

    /**
     * Check the DOM for the width of each anchor to set a width for the overflow
     */
    useEffect(() => {
        setInnerScrollWidth();
    }, [items, full, anchorIdActive, isSticky, overflowWidth, setOverflowWidth]);

    if (items.length === 0) {
        return null;
    }

    return (
        <Styled className="subnav">
            <SubNavWrap ref={subNavEl}>
                <AppBar position={isSticky ? 'fixed' : 'static'} color="inherit">
                    <SubNavInner id="navScroll" className={`${full ? 'full' : 'overflow'}`}>
                        {full ? (
                            <>
                                {isSticky ? <SubnavBackToTopBtn label={backToTopBtnLabel} /> : ''}
                                <GridContainer>
                                    <Grid
                                        container
                                        direction="row"
                                        justifyContent="center"
                                        alignItems="center"
                                        spacing={0}>
                                        {subNavItems}
                                    </Grid>
                                </GridContainer>
                                {isSticky ? (
                                    <SubnavDownloadBtn
                                        label={downloadBtnLabel}
                                        accentColor={accentColor}
                                        href={downloadHref}
                                    />
                                ) : (
                                    ''
                                )}
                            </>
                        ) : (
                            <>
                                {isSticky ? <SubnavBackToTopBtn arrowOnly /> : ''}
                                <OverflowWrap
                                    className={`${isSticky ? 'sticky' : ''}`}
                                    width={overflowWidth}>
                                    {subNavItems}
                                </OverflowWrap>
                            </>
                        )}
                    </SubNavInner>
                </AppBar>
            </SubNavWrap>
        </Styled>
    );
};
